import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AuditControlsPanel = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [dateRange, setDateRange] = useState('7days');

  const auditLogs = [
    {
      id: 1,
      timestamp: new Date(Date.now() - 30 * 60000),
      user: "Sarah Chen",
      userEmail: "sarah.chen@modularflow.com",
      action: "User Created",
      target: "James Park",
      details: "Created new shop floor worker account with task update permissions",
      ipAddress: "192.168.1.45",
      status: "success",
      category: "user_management"
    },
    {
      id: 2,
      timestamp: new Date(Date.now() - 2 * 3600000),
      user: "Linda Martinez",
      userEmail: "linda.martinez@modularflow.com",
      action: "Permission Modified",
      target: "Marcus Rodriguez",
      details: "Added quality inspection permissions to factory supervisor role",
      ipAddress: "192.168.1.12",
      status: "success",
      category: "permission_change"
    },
    {
      id: 3,
      timestamp: new Date(Date.now() - 4 * 3600000),
      user: "Emily Watson",
      userEmail: "emily.watson@modularflow.com",
      action: "Template Updated",
      target: "Standard Assembly Line Workflow v2.3",
      details: "Modified task dependencies and updated resource allocation parameters",
      ipAddress: "192.168.1.78",
      status: "success",
      category: "template_change"
    },
    {
      id: 4,
      timestamp: new Date(Date.now() - 6 * 3600000),
      user: "System",
      userEmail: "system@modularflow.com",
      action: "Backup Completed",
      target: "Daily Backup",
      details: "Automated daily backup completed successfully with 2.4GB data archived",
      ipAddress: "127.0.0.1",
      status: "success",
      category: "system_operation"
    },
    {
      id: 5,
      timestamp: new Date(Date.now() - 8 * 3600000),
      user: "Marcus Rodriguez",
      userEmail: "marcus.rodriguez@modularflow.com",
      action: "Login Failed",
      target: "Authentication System",
      details: "Failed login attempt with incorrect password (3rd attempt)",
      ipAddress: "192.168.1.89",
      status: "warning",
      category: "security_event"
    },
    {
      id: 6,
      timestamp: new Date(Date.now() - 12 * 3600000),
      user: "Sarah Chen",
      userEmail: "sarah.chen@modularflow.com",
      action: "Settings Changed",
      target: "System Configuration",
      details: "Updated notification rules and modified session timeout to 30 minutes",
      ipAddress: "192.168.1.45",
      status: "success",
      category: "configuration_change"
    },
    {
      id: 7,
      timestamp: new Date(Date.now() - 18 * 3600000),
      user: "Robert Kim",
      userEmail: "robert.kim@modularflow.com",
      action: "Integration Test",
      target: "MES System",
      details: "Connection test performed on MES integration endpoint",
      ipAddress: "192.168.1.56",
      status: "success",
      category: "integration_activity"
    },
    {
      id: 8,
      timestamp: new Date(Date.now() - 24 * 3600000),
      user: "System",
      userEmail: "system@modularflow.com",
      action: "Data Retention",
      target: "Audit Logs",
      details: "Archived audit logs older than 365 days per retention policy",
      ipAddress: "127.0.0.1",
      status: "success",
      category: "system_operation"
    }
  ];

  const actionOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'user_created', label: 'User Created' },
    { value: 'permission_modified', label: 'Permission Modified' },
    { value: 'template_updated', label: 'Template Updated' },
    { value: 'settings_changed', label: 'Settings Changed' },
    { value: 'login_failed', label: 'Login Failed' },
    { value: 'backup_completed', label: 'Backup Completed' }
  ];

  const userOptions = [
    { value: 'all', label: 'All Users' },
    { value: 'sarah_chen', label: 'Sarah Chen' },
    { value: 'linda_martinez', label: 'Linda Martinez' },
    { value: 'emily_watson', label: 'Emily Watson' },
    { value: 'marcus_rodriguez', label: 'Marcus Rodriguez' },
    { value: 'system', label: 'System' }
  ];

  const dateRangeOptions = [
    { value: '24hours', label: 'Last 24 Hours' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' }
  ];

  const filteredLogs = auditLogs?.filter(log => {
    const matchesSearch = log?.action?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         log?.target?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         log?.details?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesAction = selectedAction === 'all' || log?.action?.toLowerCase()?.replace(' ', '_') === selectedAction;
    const matchesUser = selectedUser === 'all' || log?.user?.toLowerCase()?.replace(' ', '_') === selectedUser;
    return matchesSearch && matchesAction && matchesUser;
  });

  const formatTimestamp = (date) => {
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-success/10 text-success';
      case 'warning':
        return 'bg-warning/10 text-warning';
      case 'error':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getActionIcon = (category) => {
    switch (category) {
      case 'user_management':
        return 'UserPlus';
      case 'permission_change':
        return 'Shield';
      case 'template_change':
        return 'FileEdit';
      case 'configuration_change':
        return 'Settings';
      case 'security_event':
        return 'AlertTriangle';
      case 'system_operation':
        return 'Server';
      case 'integration_activity':
        return 'Link';
      default:
        return 'Activity';
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
              <Icon name="Activity" size={20} color="var(--color-primary)" />
            </div>
            <span className="text-sm text-muted-foreground">Total Events</span>
          </div>
          <p className="text-2xl md:text-3xl font-semibold text-foreground">1,247</p>
          <p className="caption text-success mt-1">+12% from last week</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-success/10">
              <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
            </div>
            <span className="text-sm text-muted-foreground">Successful</span>
          </div>
          <p className="text-2xl md:text-3xl font-semibold text-foreground">1,189</p>
          <p className="caption text-muted-foreground mt-1">95.3% success rate</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-warning/10">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            </div>
            <span className="text-sm text-muted-foreground">Warnings</span>
          </div>
          <p className="text-2xl md:text-3xl font-semibold text-foreground">47</p>
          <p className="caption text-warning mt-1">Requires attention</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-error/10">
              <Icon name="XCircle" size={20} color="var(--color-error)" />
            </div>
            <span className="text-sm text-muted-foreground">Failed</span>
          </div>
          <p className="text-2xl md:text-3xl font-semibold text-foreground">11</p>
          <p className="caption text-error mt-1">Critical issues</p>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            type="search"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
          />
          <Select
            options={actionOptions}
            value={selectedAction}
            onChange={setSelectedAction}
            placeholder="Filter by action"
          />
          <Select
            options={userOptions}
            value={selectedUser}
            onChange={setSelectedUser}
            placeholder="Filter by user"
          />
          <Select
            options={dateRangeOptions}
            value={dateRange}
            onChange={setDateRange}
            placeholder="Date range"
          />
        </div>
        <Button variant="outline" iconName="Download" iconPosition="left">
          Export Logs
        </Button>
      </div>
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground">Timestamp</th>
                <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground">User</th>
                <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground">Action</th>
                <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground hidden lg:table-cell">Target</th>
                <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground hidden xl:table-cell">IP Address</th>
                <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                <th className="px-4 md:px-6 py-3 text-right text-sm font-semibold text-foreground">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredLogs?.map((log) => (
                <tr key={log?.id} className="hover:bg-muted/50 transition-colors duration-250">
                  <td className="px-4 md:px-6 py-4">
                    <span className="caption text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(log?.timestamp)}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{log?.user}</p>
                      <p className="caption text-muted-foreground truncate">{log?.userEmail}</p>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Icon name={getActionIcon(log?.category)} size={16} color="var(--color-muted-foreground)" />
                      <span className="text-sm text-foreground">{log?.action}</span>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-4 hidden lg:table-cell">
                    <span className="text-sm text-muted-foreground line-clamp-1">{log?.target}</span>
                  </td>
                  <td className="px-4 md:px-6 py-4 hidden xl:table-cell">
                    <span className="caption text-muted-foreground data-text">{log?.ipAddress}</span>
                  </td>
                  <td className="px-4 md:px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(log?.status)}`}>
                      {log?.status}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-4">
                    <button
                      className="flex items-center justify-end gap-1 text-primary hover:text-primary/80 transition-colors duration-250"
                      onClick={() => console.log('View details', log?.id)}
                    >
                      <span className="caption">View</span>
                      <Icon name="ChevronRight" size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 md:px-6 py-4 bg-card rounded-xl border border-border">
        <span className="caption text-muted-foreground">
          Showing {filteredLogs?.length} of {auditLogs?.length} audit logs
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="ChevronLeft" disabled>
            Previous
          </Button>
          <Button variant="outline" size="sm" iconName="ChevronRight">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditControlsPanel;